import React, {useState, useRef, useEffect} from 'react';
import {Loader, AlertCircle, BarChart3} from 'lucide-react';
import {Link, useNavigate, useParams} from "react-router-dom";
import {Controller, useForm} from "react-hook-form";
import {useMutation, useQuery} from '@tanstack/react-query';
import secureStorage from "../utils/secureStorage";
import {api} from "../config/api";
import FormListbox from "./../components/Common/FormListbox";
import ClientTypeEditor from "./../components/Setting/ClientTypeEditor";
import ClientLoyaltyCard from "./../components/Loyalty/ClientLoyaltyCard";
import {FormHeader, Field, Section, ClientPhoto, inputClass, initialsOf, COUNTRY_CODES, CIVILITES} from "./clientFormUI";
import Button from "../components/Common/Button";
import Notification from "../components/Common/Notification";
import DateInput from "../components/Common/DateInput";

const ClientEditForm = () => {
    const navigate = useNavigate();
    const {clientId} = useParams();
    const [clientImage, setClientImage] = useState(null);
    const [showClientTypeEditor, setShowClientTypeEditor] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        type: '',
        message: ''
    });

    const {
        register,
        control,
        handleSubmit,
        formState: {errors},
        watch,
        setValue
    } = useForm();

    const formRef = useRef(null);
    const fileInputRef = useRef(null);

    // Client types (VIP, VVIP, ...) are configured per account in Paramètres > Types de clients
    const {data: clientTypes = []} = useQuery({
        queryKey: ['client-types', 'all'],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/client-types?itemsPerPage=100', {
                headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
            });
            return response.data.items || [];
        }
    });

    // Fetch client data (unchanged)
    const {data: client, isLoading: isClientLoading, error: clientError} = useQuery({
        queryKey: ['client', clientId],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get(`/clients/${clientId}`, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                }
            });
            return response.data;
        },
        onSuccess: (data) => {
            console.log("Client data received:", data);

            setTimeout(() => {
                setValue('firstName', data.firstName);
                setValue('lastName', data.lastName);
                setValue('email', data.email);
                setValue('countryCode', data.countryCode);
                setValue('phone', data.phone);
                setValue('address', data.adresse);
                setValue('city', data.city);
                setValue('civilite', data.civilite);
                setValue('clientTypeId', data.clientTypeId);

                if (data.birthday) {
                    const birthdayDate = data.birthday.split('T')[0];
                    setValue('birthday', birthdayDate);
                }

                if (data.image) {
                    setClientImage(data.image);
                }
            }, 0);
        },
        onError: (error) => {
            console.error("Error fetching client data:", error);
        }
    });

    useEffect(() => {
        if (client) {
            setValue('firstName', client.firstName);
            setValue('lastName', client.lastName);
            setValue('email', client.email);
            setValue('countryCode', client.countryCode);
            setValue('phone', client.phone);
            setValue('address', client.adresse);
            setValue('city', client.city);
            setValue('civilite', client.civilite);
            setValue('clientTypeId', client.clientTypeId);

            if (client.birthday) {
                const birthdayDate = client.birthday.split('T')[0];
                setValue('birthday', birthdayDate);
            }
        }
    }, [client, setValue]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => setClientImage(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const showNotification = (type, message) => {
        setNotification({
            show: true,
            type,
            message
        });

        if (type === 'success') {
            setTimeout(() => {
                setNotification(prev => ({...prev, show: false}));
            }, 3000);
        }
    };

    const closeNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    // API mutation for updating a client (unchanged)
    const updateClientMutation = useMutation({
        mutationFn: async (formData) => {
            const accountId = await secureStorage.getItem("selectedAccount");

            return api.put(`/clients/${clientId}`, formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });
        },
        onSuccess: () => {
            showNotification('success', 'Client modifié avec succès!');
            setTimeout(() => {
                navigate('/clients', {replace: true});
            }, 2000);
        },
        onError: (error) => {
            console.error("Error updating client:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                'Une erreur est survenue lors de la modification du client';
            showNotification('error', errorMessage);
        }
    });

    const onSubmit = (data) => {
        console.log("Form submitted with data:", data);

        const formData = new FormData();

        formData.append("first_name", data.firstName);
        formData.append("last_name", data.lastName);
        formData.append("birthday", data.birthday);
        formData.append("adresse", data.address);
        formData.append("city", data.city);
        formData.append("country_code", data.countryCode);
        formData.append("phone", data.phone);
        formData.append("email", data.email);
        formData.append("device_name_updated", "web");
        formData.append("active", "true");
        if (data.civilite) formData.append("civilite", data.civilite);
        if (data.clientTypeId) formData.append("client_type_id", data.clientTypeId);

        if (clientImage && clientImage.startsWith('data:')) {
            fetch(clientImage)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], "client_image.png", {type: "image/png"});
                    formData.append("image", file);

                    updateClientMutation.mutate(formData);
                });
        } else {
            updateClientMutation.mutate(formData);
        }
    };

    if (isClientLoading) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="flex items-center space-x-2">
                    <Loader className="w-6 h-6 text-primary animate-spin"/>
                    <span className="text-gray-900 dark:text-gray-100">Chargement des données du client...</span>
                </div>
            </div>
        );
    }

    if (clientError) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md border border-red-200 dark:border-red-800 max-w-md">
                    <div className="flex items-center">
                        <AlertCircle className="w-6 h-6 text-red-500 mr-3"/>
                        <span className="text-red-600 dark:text-red-400">
                            Erreur lors du chargement des données: {clientError.message}
                        </span>
                    </div>
                    <div className="mt-4 flex justify-center">
                        <Link
                            to="/clients"
                            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            Retour à la liste des clients
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <FormHeader
                title="Modifier client"
                backTo="/clients"
                onSave={() => formRef.current.requestSubmit()}
                saving={updateClientMutation.isLoading}
            >
                <Button as={Link} to={`/clients/stats/${clientId}`} variant="plain" icon={BarChart3} compact>
                    Statistiques
                </Button>
            </FormHeader>

            <form ref={formRef} onSubmit={handleSubmit(onSubmit)}
                  className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8 w-full">
                <ClientPhoto
                    image={clientImage}
                    initials={initialsOf(watch("firstName"), watch("lastName"))}
                    onPick={() => fileInputRef.current?.click()}
                />
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden"/>

                <div className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <Field id="firstName" label="Prénom" required error={errors.firstName}>
                            <input id="firstName" type="text" autoComplete="off" placeholder="Prénom"
                                   className={inputClass(errors.firstName)}
                                   {...register('firstName', {required: "Le prénom est obligatoire"})}/>
                        </Field>
                        <Field id="lastName" label="Nom" required error={errors.lastName}>
                            <input id="lastName" type="text" autoComplete="off" placeholder="Nom"
                                   className={inputClass(errors.lastName)}
                                   {...register('lastName', {required: "Le nom est obligatoire"})}/>
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <FormListbox
                            label="Civilité"
                            options={CIVILITES}
                            value={watch("civilite")}
                            onChange={(id) => setValue("civilite", id)}
                            placeholder="Sélectionner"
                            required={false}
                        />
                        <FormListbox
                            label="Type de client"
                            options={clientTypes}
                            value={watch("clientTypeId")}
                            onChange={(id) => setValue("clientTypeId", id)}
                            placeholder="Standard"
                            required={false}
                            labelAction={
                                <button
                                    type="button"
                                    onClick={() => setShowClientTypeEditor(true)}
                                    className="text-xs font-medium text-primary hover:text-primary-dark"
                                >
                                    + Nouveau type
                                </button>
                            }
                        />
                    </div>

                    <Section>
                        <Field id="email" label="Email" required error={errors.email}>
                            <input id="email" type="email" autoComplete="off" placeholder="client@example.com"
                                   className={inputClass(errors.email)}
                                   {...register('email', {
                                       required: "L'email est obligatoire",
                                       pattern: {
                                           value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                           message: "Adresse email invalide"
                                       }
                                   })}/>
                        </Field>

                        <Field id="phone" label="Téléphone" required error={errors.phone}>
                            <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
                                <div className="w-full sm:w-36">
                                    <FormListbox
                                        options={COUNTRY_CODES}
                                        value={watch("countryCode") || "212"}
                                        onChange={(id) => setValue("countryCode", id)}
                                        error={errors.countryCode?.message}
                                        placeholder="Code pays"
                                        required={false}
                                        displayValue={(item) => `${item.flag} ${item.name}`}
                                    />
                                </div>
                                <input id="phone" type="text" autoComplete="off" placeholder="Numéro de téléphone"
                                       className={`flex-1 min-w-0 ${inputClass(errors.phone)}`}
                                       {...register('phone', {
                                           required: "Le numéro est obligatoire",
                                           pattern: {
                                               value: /^[0-9-]{3,15}$/,
                                               message: "Format de téléphone invalide"
                                           }
                                       })}/>
                            </div>
                        </Field>
                    </Section>

                    <Section>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <Field id="address" label="Adresse" required error={errors.address}>
                                <textarea id="address" rows="3" placeholder="Adresse"
                                          className={inputClass(errors.address)}
                                          {...register('address', {required: "L'adresse est obligatoire"})}/>
                            </Field>
                            <Field id="city" label="Ville" required error={errors.city}>
                                <input id="city" type="text" autoComplete="off" placeholder="Ville"
                                       className={inputClass(errors.city)}
                                       {...register('city', {required: "La ville est obligatoire"})}/>
                            </Field>
                        </div>

                        <Field id="birthday" label="Date de naissance" required error={errors.birthday}>
                            <Controller name="birthday" control={control}
                                        rules={{required: "La date de naissance est obligatoire"}}
                                        render={({field}) => (
                                            <DateInput id="birthday" required value={field.value || ''} name={field.name}
                                                       max={new Date().toISOString().slice(0, 10)}
                                                       onChange={(e) => field.onChange(e.target.value)}
                                                       className={inputClass(errors.birthday)}/>
                                        )}/>
                        </Field>
                    </Section>
                </div>
            </form>

            <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-6 sm:pb-8 w-full">
                <ClientLoyaltyCard
                    clientId={clientId}
                    clientName={`${watch("firstName") || ""} ${watch("lastName") || ""}`.trim()}
                />
            </div>

            {/* Quick create client type */}
            {showClientTypeEditor && (
                <ClientTypeEditor
                    clientType={null}
                    onClose={() => setShowClientTypeEditor(false)}
                    showNotification={showNotification}
                    onSaved={(created) => setValue("clientTypeId", created.id)}
                />
            )}

            {/* Notification */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={closeNotification}
            />

            {/* Loading overlay */}
            {updateClientMutation.isLoading && (
                <div className="fixed inset-0 z-40 bg-black bg-opacity-20 flex items-center justify-center">
                    <div className="mx-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg flex items-center">
                        <Loader className="w-5 h-5 text-primary animate-spin mr-3"/>
                        <span className="text-gray-900 dark:text-gray-100">Mise à jour du client en cours...</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientEditForm;
